import { GetStaticPaths, GetStaticProps } from "next";
import { Fragment, FunctionComponent } from "react";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { ShippingCopy, QuantityGuide } from "../../../../lib/sanity-schema";
import { ComponentIdInjector } from "../../../../lib/component-id-injector";
import renderModule from "../../../../lib/render-module";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";
import { toFeaturedProductProps } from "../../../../lib/transformers/featured-product";
import sanityClient from "../../../../lib/sanity-client";
import { quantityGuideQuery } from "../../../../lib/sanity/product-queries";
import { FeaturedProductWrapper } from "../../../../components/FeaturedProductWrapper";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  getDefaultStaticPathsMiniCart,
  getDefaultStaticPropsMiniCart,
} from "../../../../lib/utils";
import { CartBaseProps } from "../../../../lib/sanity/shared";
import {
  buildSanityQuery as buildSubscriptionQuery,
  SanityQueryType,
  SanitySubscriptionPage,
} from "../../../../lib/sanity/subscription-queries";
import {
  TranslationGroupSanityType,
  buildTranslationGroupQuery,
  toCopyObject,
} from "../../../../lib/sanity/translation-group-queries";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import { SubscriptionQuizCopy } from "../../../../components/Quiz";
import { ConversionCopy } from "../product/[slug]";

interface PageData extends CartBaseProps, SanityQueryType {
  shopcardCopy: {
    copy: ConversionCopy;
    shippingCopy: ShippingCopy;
  };
  subscriptionQuizCopy: SubscriptionQuizCopy;
  quantityGuide: QuantityGuide;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCart(context);
  const { lang, region } = defaultProps;

  const query = buildSubscriptionQuery(lang);
  const sanityQueryResult = await sanityClient().fetch<SanityQueryType>(query);
  const { shippingCopy, subscriptionPage } = sanityQueryResult;

  const conversionTranslationGroupQuery = buildTranslationGroupQuery(
    lang,
    "Conversion",
  );

  const conversionCopy = await sanityClient()
    .fetch<TranslationGroupSanityType>(conversionTranslationGroupQuery)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<ConversionCopy>(lang, translations),
    );

  const subscriptionQuizTranslationGroupQuery = buildTranslationGroupQuery(
    lang,
    "SubscriptionQuiz",
  );
  const subscriptionQuizCopy = await sanityClient()
    .fetch<TranslationGroupSanityType>(subscriptionQuizTranslationGroupQuery)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<SubscriptionQuizCopy>(lang, translations),
    );

  // Quantity guide shared with the PDP, used by the Featured Product module.
  const quantityGuide =
    await sanityClient().fetch<QuantityGuide>(quantityGuideQuery);

  await resolveTabbedCollections(subscriptionPage.modules, { lang, region });

  return {
    props: {
      ...sanityQueryResult,
      ...defaultProps,
      shopcardCopy: {
        copy: conversionCopy,
        shippingCopy,
      },
      subscriptionQuizCopy,
      quantityGuide,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPathsMiniCart;

function validate(subscriptionPage: SanitySubscriptionPage) {
  if (!subscriptionPage?.title) {
    throw Error("Subscription Page must have a title.");
  }
}

const SubscriptionPage: FunctionComponent<PageData> = ({
  subscriptionPage,
  subscribableProducts,
  shopcardCopy,
  subscriptionQuizCopy,
  quantityGuide,
  ...miniCartLayoutWrapperProps
}) => {
  validate(subscriptionPage);
  const {
    region,
    lang,
    appPlayLinks,
    giftCardFormCopy,
    navAndCartInfoBanner,
    productRecs,
    aria,
  } = miniCartLayoutWrapperProps;

  const miniCartDialog = (
    <MiniCartDialog
      productRecs={productRecs}
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={miniCartLayoutWrapperProps.cart}
      navAndCartInfoBanner={navAndCartInfoBanner}
      region={region}
      lang={lang}
      subscribableProducts={subscribableProducts}
    />
  );

  const renderSubscriptionModule = (
    module: SanitySubscriptionPage["modules"][number],
  ) => {
    try {
      if (module._type === "featuredProduct") {
        return (
          <ComponentIdInjector id={module.anchorId}>
            <FeaturedProductWrapper
              moduleProps={toFeaturedProductProps(
                module,
                shopcardCopy.copy,
                quantityGuide,
                lang,
              )}
              loadingMessage={shopcardCopy.copy.adding}
            />
          </ComponentIdInjector>
        );
      }

      const moduleWithCopy =
        module._type === "quiz" ? { ...module, subscriptionQuizCopy } : module;

      return renderModule({
        module: moduleWithCopy,
        region,
        lang,
        appPlayLinks,
        shopcardCopy,
        aria,
      });
    } catch (error) {
      console.warn(`[subscriptions] Skipping module "${module._type}":`, error);
      return null;
    }
  };

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="subscriptions" />
      <LayoutWrapper
        {...{
          ...miniCartLayoutWrapperProps,
          pageTitle: subscriptionPage.title[lang],
          metaTags: subscriptionPage.metaTags,
          slideOutContent: [miniCartDialog],
          openMiniCartPanel: Boolean(miniCartDialog),
        }}
        isTransparent={false}
      >
        {subscriptionPage.modules?.map((module) => (
          <Fragment key={module._key}>
            {renderSubscriptionModule(module)}
          </Fragment>
        ))}
      </LayoutWrapper>
    </>
  );
};

export default SubscriptionPage;
